import { useMemo, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiEndpoints, routesPaths } from '@selfserve/react-utils';
import { usePost } from './use-api';
import { useStorage, storage, storageKeys } from './use-storage';
import { useLog } from './use-log';

export const useLogoutRequest = () => usePost<any, any>(apiEndpoints.logout);

const readAccessTokenFlag = (): boolean | null => {
  try {
    return JSON.parse(
      window.localStorage.getItem(storageKeys.HAS_ACCESS_TOKEN) || 'null'
    );
  } catch {
    return null;
  }
};

interface IReturn {
  token: string;
  hasAccessToken: boolean;
  setHasAccessToken: (hasAccessToken: boolean) => void;
  setToken: (token: string, rememberMe?: boolean) => void;
  clearToken: () => void;
  logout: (callback?: () => void) => void;
}

export const useAuth = (): IReturn => {
  const { log } = useLog();
  const navigate = useNavigate();
  const { mutateAsync: handleLogoutRequest } = useLogoutRequest();
  const [, setPartnerId] = useStorage(storage.LOCAL, storageKeys.PARTNER_ID);
  const [, setQueryParams] = useStorage(
    storage.LOCAL,
    storageKeys.QUERY_PARAMS
  );
  const [sessionToken, setSessionToken] = useStorage(
    storage.SESSION,
    storageKeys.TOKEN
  );
  const [localToken, setLocalToken] = useStorage(
    storage.LOCAL,
    storageKeys.TOKEN
  );
  // not useStorage - the flag is synced from other tabs
  // the sync must only update the state without writing back to the storage
  const [accessTokenFlag, setAccessTokenFlag] = useState<boolean | null>(
    readAccessTokenFlag
  );
  const setHasAccessToken = useCallback((hasAccessToken: boolean) => {
    try {
      window.localStorage.setItem(
        storageKeys.HAS_ACCESS_TOKEN,
        JSON.stringify(hasAccessToken)
      );
    } catch {
      // localStorage may be full or blocked - must not fail the caller
    }
    setAccessTokenFlag(hasAccessToken);
  }, []);
  const token = useMemo(
    () => sessionToken || localToken,
    [sessionToken, localToken]
  );
  // the access_token cookie is httpOnly - the access_token flag is the only client side hint of an existing access_token cookie
  // fall back to a stored token only when the flag was never set
  const hasAccessToken = useMemo(
    () => accessTokenFlag ?? !!token,
    [token, accessTokenFlag]
  );

  const setToken = useCallback(
    (token: string, rememberMe?: boolean) => {
      clearToken();
      setQueryParams(null);
      if (rememberMe) {
        setLocalToken(token);
      } else {
        setSessionToken(token);
      }
      setHasAccessToken(true);
    },
    [setSessionToken, setLocalToken, setHasAccessToken]
  );

  const clearToken = useCallback(() => {
    setSessionToken('');
    setLocalToken('');
    setHasAccessToken(false);
  }, [setSessionToken, setLocalToken, setHasAccessToken]);

  const logout = useCallback(
    (callback?: () => void) => {
      if (typeof callback === 'function') callback();
      clearToken();
      setPartnerId(null);
      setQueryParams(null);
      handleLogoutRequest({});
      navigate(routesPaths.LOGIN);
    },
    [clearToken, handleLogoutRequest, navigate]
  );

  // keep the access_token flag in sync when logging in or out in another tab
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (
        event.storageArea !== window.localStorage ||
        event.key !== storageKeys.HAS_ACCESS_TOKEN
      ) {
          return;
      }
      // read the flag current value rather than event.newValue - setToken writes false then true
      setAccessTokenFlag(readAccessTokenFlag());
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    log('sessionToken: ', sessionToken);
    log('localToken: ', localToken);
  }, [sessionToken, localToken, log]);

  return {
    token,
    hasAccessToken,
    setHasAccessToken,
    setToken,
    clearToken,
    logout,
  };
};
